import React, { useLayoutEffect, useRef, useState } from 'react'
import type { PopperChildrenProps, ReferenceChildrenProps } from 'react-popper'
import { Manager, Popper, Reference } from 'react-popper'

import { TooltipBox } from './TooltipBox'
import type { Props } from './types'

export const Tooltip = ({
  placement = 'top',
  content,
  children,
  size = 'large',
  strategy = 'absolute',
  onOpen,
  onClose,
}: Props) => {
  const [isVisible, setIsVisible] = useState(false)
  const positionUpdateRef = useRef<Function | null>(null)

  useLayoutEffect(() => {
    if (positionUpdateRef.current) {
      positionUpdateRef.current()
    }
  }, [content])

  const showTip = () => {
    if (onOpen) {
      onOpen()
    }

    setIsVisible(true)
  }

  const hideTip = () => {
    if (onClose) {
      onClose()
    }

    setIsVisible(false)
  }

  const renderReferenceChildren = ({ ref }: ReferenceChildrenProps) => {
    return (
      <div id="tooltip-reference" ref={ref}>
        {children}
      </div>
    )
  }

  const renderPopperChildren = ({
    ref,
    style,
    placement: dataPlacement,
    update,
  }: PopperChildrenProps) => {
    positionUpdateRef.current = update

    return (
      <div
        ref={ref}
        data-placement={dataPlacement}
        style={{ ...style, paddingTop: 4, paddingBottom: 4, zIndex: 1 }}
      >
        <TooltipBox size={size}>{content}</TooltipBox>
      </div>
    )
  }

  return (
    <Manager>
      <div
        id="tooltip-container"
        style={{ color: 'inherit' }}
        onMouseEnter={showTip}
        onTouchStart={showTip}
        onMouseLeave={hideTip}
        onTouchEnd={hideTip}
      >
        <Reference>{renderReferenceChildren}</Reference>
        {isVisible && (
          <Popper
            placement={placement}
            modifiers={[
              { name: 'preventOverflow', enabled: false },
              { name: 'hide', enabled: false },
            ]}
            strategy={strategy}
          >
            {renderPopperChildren}
          </Popper>
        )}
      </div>
    </Manager>
  )
}
